import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  AlarmClock,
  BellOff,
  CheckCheck,
  CircleSlash,
  ExternalLink,
  EyeOff,
  Hourglass,
  RefreshCw,
  Search,
  Timer,
  Undo2,
  User,
} from "lucide-react";
import { useWaitingResponse } from "../hooks/useWaitingResponse";
import { useConversationsApi } from "../api/conversationsApi";
import { contactLabel, formatPhone } from "../components/analysis/analysisLabels";
import { formatDuration, getStatusColor } from "../utils/helpers";

// Um predicado por visão: os cartões e os atalhos abaixo deles apontam para as
// mesmas chaves, então o número que a pessoa lê é exatamente o que ela filtra.
const VIEWS = {
  overdue: (item) => item.overdue,
  ontime: (item) => item.counted && !item.overdue,
  never: (item) => item.counted && item.neverAnswered,
  counted: (item) => item.counted,
  acknowledged: (item) => item.acknowledged,
  ignored: (item) => !item.slaEnabled,
};

const SORTS = {
  wait: { label: "Maior espera", compare: (a, b) => b.waitingMinutes - a.waitingMinutes },
  recent: {
    label: "Mensagem mais recente",
    compare: (a, b) => new Date(b.lastIncomingAt) - new Date(a.lastIncomingAt),
  },
  messages: {
    label: "Mais mensagens sem resposta",
    compare: (a, b) => b.pendingMessages - a.pendingMessages,
  },
};

const WEEKDAY_LABEL = { 1: "seg", 2: "ter", 3: "qua", 4: "qui", 5: "sex", 6: "sáb", 7: "dom" };

const clock = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const StatCard = ({ icon: Icon, tone, value, label, hint, alert, active, onClick, children }) => {
  const Wrapper = onClick ? "button" : "div";

  return (
    <Wrapper
      onClick={onClick}
      className={`rounded-lg border bg-white p-4 text-left shadow-sm transition-colors dark:bg-gray-900 ${
        onClick ? "cursor-pointer hover:border-gray-400 dark:hover:border-gray-500" : ""
      } ${
        active
          ? "border-green-500 ring-2 ring-green-500/40 dark:border-green-500"
          : alert
            ? "border-red-300 dark:border-red-800"
            : "border-gray-200 dark:border-gray-700"
      }`}
      title={onClick ? `Filtrar por ${label.toLowerCase()}` : undefined}
    >
      <div className="mb-1 flex items-center justify-between">
        <Icon className={`h-5 w-5 ${tone}`} />
        <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</span>
      </div>
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</p>
      {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
      {children}
    </Wrapper>
  );
};

const Chip = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
    }`}
  >
    {children}
  </button>
);

const WaitingRow = ({
  item,
  slaMinutes,
  busy,
  selected,
  onToggleSelect,
  onOpenConversation,
  onSlaChange,
}) => {
  const name = contactLabel(item.contact);
  const phone = formatPhone(item.contact?.phoneNumber);
  // A barra satura no dobro do prazo: passando disso o comprimento não informa
  // mais nada, o número ao lado é que carrega a gravidade.
  const ratio = Math.min(item.waitingMinutes / Math.max(slaMinutes, 1), 2) / 2;

  const edge = !item.counted
    ? "border-l-4 border-l-gray-300 dark:border-l-gray-600"
    : item.overdue
      ? "border-l-4 border-l-red-500"
      : "border-l-4 border-l-amber-400";

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900 ${edge} ${
        item.counted ? "" : "opacity-75"
      }`}
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 gap-3">
          <input
            type="checkbox"
            checked={selected}
            onChange={() => onToggleSelect(item.conversationId)}
            className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-green-500"
            aria-label={`Selecionar ${name}`}
          />

          <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-semibold text-gray-900 dark:text-gray-100">{name}</p>

            {item.overdue && (
              <span className="rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800 dark:bg-red-900/40 dark:text-red-300">
                {formatDuration(item.overdueBy * 60)} além do prazo
              </span>
            )}

            {item.acknowledged && (
              <span className="flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                <CheckCheck className="h-3 w-3" />
                Visto
              </span>
            )}

            {!item.slaEnabled && (
              <span className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                <BellOff className="h-3 w-3" />
                Fora da contagem
              </span>
            )}

            {item.neverAnswered && (
              <span className="flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                <CircleSlash className="h-3 w-3" />
                Nunca respondido
              </span>
            )}

            {item.pendingMessages > 1 && (
              <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {item.pendingMessages} sem resposta
              </span>
            )}

            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${getStatusColor(item.status)}`}>
              {item.status}
            </span>
          </div>

          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {phone && phone !== name && `${phone} · `}
            desde{" "}
            {new Date(item.waitingSince).toLocaleString("pt-BR", {
              day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
            })}
            {item.waitingMinutes !== item.clockMinutes &&
              ` · ${formatDuration(item.clockMinutes * 60)} de relógio`}
            {item.minutesSinceLastReply !== null &&
              ` · última resposta há ${formatDuration(item.minutesSinceLastReply * 60)}`}
            {item.funnel && ` · ${item.funnel}`}
          </p>

          {item.tags?.length > 0 && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {item.tags.map((tag) => (
                <span
                  key={tag.id}
                  className="rounded-full px-2 py-0.5 text-xs font-medium"
                  style={{ backgroundColor: tag.backgroundColor, color: tag.textColor }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
          </div>
        </div>

        <div className="text-right">
          <p className="text-xs text-gray-500 dark:text-gray-400">Esperando</p>
          <p
            className={`text-xl font-bold tabular-nums ${
              item.overdue ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-gray-100"
            }`}
          >
            {clock(item.waitingMinutes)}
          </p>
        </div>
      </div>

      <div className="mb-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div
          className={`h-full ${
            !item.counted ? "bg-gray-400" : item.overdue ? "bg-red-500" : "bg-amber-400"
          }`}
          style={{ width: `${Math.max(ratio * 100, 4)}%` }}
        />
      </div>

      <p className="mb-3 line-clamp-2 text-sm text-gray-700 dark:text-gray-300">“{item.preview}”</p>

      <div className="flex flex-wrap items-center gap-2">
        {onOpenConversation && (
          <button
            onClick={() => onOpenConversation(item.conversationId)}
            className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-600"
          >
            <ExternalLink className="h-4 w-4" />
            Responder agora
          </button>
        )}

        <button
          onClick={() => onSlaChange(item.conversationId, { acknowledge: !item.acknowledged })}
          disabled={busy}
          className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          title={
            item.acknowledged
              ? "Voltar para a fila de prazo"
              : "Sai da fila até o cliente mandar outra mensagem"
          }
        >
          {item.acknowledged ? <Undo2 className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          {item.acknowledged ? "Desmarcar" : "Marcar como visto"}
        </button>

        <button
          onClick={() => onSlaChange(item.conversationId, { enabled: !item.slaEnabled })}
          disabled={busy}
          className={`flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm transition-colors disabled:opacity-60 ${
            item.slaEnabled
              ? "border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300"
          }`}
          title={
            item.slaEnabled
              ? "Esta conversa deixa de contar prazo até você religar"
              : "Voltar a contar prazo nesta conversa"
          }
        >
          <BellOff className="h-4 w-4" />
          {item.slaEnabled ? "Tirar da contagem" : "Voltar a contar"}
        </button>

        {item.assignedUsers?.length > 0 ? (
          <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <User className="h-3.5 w-3.5" />
            {item.assignedUsers.map((user) => user.name).join(", ")}
          </span>
        ) : (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            Sem responsável atribuído
          </span>
        )}
      </div>
    </div>
  );
};

const WaitingResponse = ({ onOpenConversation }) => {
  const [view, setView] = useState("overdue");
  const [sort, setSort] = useState("wait");
  const [search, setSearch] = useState("");
  const [owner, setOwner] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const { waiting, loading, error, refetch } = useWaitingResponse();
  const api = useConversationsApi();

  const toggleSelect = (conversationId) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(conversationId)) next.delete(conversationId);
      else next.add(conversationId);
      return next;
    });

  // Trocar de aba muda o conjunto visível; manter a seleção antiga faria a ação
  // em lote atingir conversas que sumiram da tela.
  const changeView = (value) => {
    setView(value);
    setSelected(new Set());
  };

  const owners = useMemo(() => {
    const map = new Map();
    for (const item of waiting?.items || []) {
      for (const user of item.assignedUsers || []) map.set(user.id, user.name);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [waiting]);

  const items = useMemo(() => {
    if (!waiting) return [];
    const term = search.trim().toLowerCase();

    const matches = VIEWS[view] || VIEWS.counted;
    const byView = waiting.items.filter(matches);

    const byOwner = byView.filter((item) => {
      if (owner === "all") return true;
      if (owner === "none") return !item.assignedUsers?.length;
      return item.assignedUsers?.some((user) => String(user.id) === owner);
    });

    const filtered = term
      ? byOwner.filter((item) =>
          [item.contact?.name, item.contact?.phoneNumber, item.preview]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term)),
        )
      : byOwner;

    return [...filtered].sort(SORTS[sort].compare);
  }, [waiting, view, owner, search, sort]);

  const handleSlaChange = async (conversationId, payload) => {
    setBusyId(conversationId);
    try {
      await api.setSlaState(conversationId, payload);
      await refetch();
      toast.success(
        payload.acknowledge === true ? "Marcada como vista."
          : payload.acknowledge === false ? "De volta à fila de prazo."
            : payload.enabled ? "Conversa voltou a contar prazo."
              : "Conversa fora da contagem de prazo.",
      );
    } catch (err) {
      toast.error(err.message || "Não foi possível atualizar a conversa.");
    } finally {
      setBusyId(null);
    }
  };

  const handleBulk = async (payload, describe) => {
    const ids = items
      .map((item) => item.conversationId)
      .filter((id) => selected.has(id));

    if (!ids.length) return;

    setBulkBusy(true);
    try {
      const result = await api.setSlaStateBulk(ids, payload);
      await refetch();
      setSelected(new Set());
      toast.success(`${result.updated} ${describe}`);
      if (result.updated < result.requested) {
        toast(
          `${result.requested - result.updated} conversa(s) não foram alteradas por falta de acesso.`,
          { icon: "⚠️" },
        );
      }
    } catch (err) {
      toast.error(err.message || "Não foi possível aplicar a ação em lote.");
    } finally {
      setBulkBusy(false);
    }
  };

  if (loading && !waiting) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-gray-600 dark:text-gray-400">Carregando fila de resposta...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center bg-gray-50 dark:bg-gray-800">
        <p className="text-red-500">Erro ao carregar a fila: {error}</p>
      </div>
    );
  }

  if (!waiting) return null;

  const { sla, summary } = waiting;
  const hours = sla.businessHours;
  const visibleSelected = items.filter((item) => selected.has(item.conversationId)).length;
  const allVisibleSelected = items.length > 0 && visibleSelected === items.length;
  const someVisibleSelected = visibleSelected > 0;

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 p-6 transition-colors duration-300 dark:bg-gray-800">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Aguardando Resposta
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Prazo de {formatDuration(sla.minutes * 60)}
              {hours.enabled
                ? ` · contado das ${clock(hours.startMinute)} às ${clock(hours.endMinute)}, ${hours.weekdays
                    .map((day) => WEEKDAY_LABEL[day])
                    .join(", ")}`
                : " · contado 24 horas por dia"}
              {" · atualiza sozinho a cada minuto"}
            </p>
          </div>

          <button
            onClick={refetch}
            className="flex items-center gap-1.5 rounded-lg bg-green-500 px-4 py-2 text-sm text-white transition-colors hover:bg-green-600"
          >
            <RefreshCw className="h-4 w-4" />
            Atualizar
          </button>
        </div>

        <div className="mb-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            icon={AlarmClock}
            tone="text-red-500"
            value={summary.overdue}
            label="Fora do prazo"
            hint={`de ${summary.waiting} na contagem`}
            alert={summary.overdue > 0}
            active={view === "overdue"}
            onClick={() => changeView("overdue")}
          />
          <StatCard
            icon={Hourglass}
            tone="text-amber-500"
            value={summary.waiting - summary.overdue}
            label="Dentro do prazo"
            hint="Ainda dá tempo"
            active={view === "ontime"}
            onClick={() => changeView("ontime")}
          />
          <StatCard
            icon={CircleSlash}
            tone="text-orange-500"
            value={summary.neverAnswered}
            label="Nunca respondidos"
            hint="Primeiro contato sem retorno"
            active={view === "never"}
            onClick={() => changeView("never")}
          />
          <StatCard
            icon={Timer}
            tone="text-blue-500"
            value={clock(summary.worstWaitMinutes)}
            label="Maior espera"
            hint="Horas:minutos no topo da fila"
          />
        </div>

        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Chip active={view === "counted"} onClick={() => changeView("counted")}>
              Todas aguardando ({summary.waiting})
            </Chip>

            {summary.acknowledged > 0 && (
              <Chip
                active={view === "acknowledged"}
                onClick={() => changeView("acknowledged")}
              >
                Vistas ({summary.acknowledged})
              </Chip>
            )}

            {summary.ignored > 0 && (
              <Chip active={view === "ignored"} onClick={() => changeView("ignored")}>
                Fora da contagem ({summary.ignored})
              </Chip>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={owner}
              onChange={(event) => setOwner(event.target.value)}
              className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Filtrar por responsável"
            >
              <option value="all">Todos os responsáveis</option>
              <option value="none">Sem responsável</option>
              {owners.map((user) => (
                <option key={user.id} value={String(user.id)}>
                  {user.name}
                </option>
              ))}
            </select>

            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
              className="rounded-lg border border-gray-300 bg-white py-2 pl-3 pr-8 text-sm text-gray-900 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Ordenar"
            >
              {Object.entries(SORTS).map(([value, option]) => (
                <option key={value} value={value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="relative w-56">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Cliente, telefone, mensagem..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
              />
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                className="h-4 w-4 cursor-pointer accent-green-500"
                checked={allVisibleSelected}
                // Sem isso, marcar 3 de 10 deixaria a caixa vazia, como se nada
                // estivesse selecionado.
                ref={(node) => {
                  if (node) node.indeterminate = someVisibleSelected && !allVisibleSelected;
                }}
                onChange={() =>
                  setSelected(
                    allVisibleSelected
                      ? new Set()
                      : new Set(items.map((item) => item.conversationId)),
                  )
                }
                disabled={items.length === 0}
              />
              Selecionar todos
            </label>

            <p className="ml-auto text-sm text-gray-600 dark:text-gray-400">
              {items.length} conversa{items.length === 1 ? "" : "s"}
            </p>
          </div>

          {selected.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-3 dark:border-green-800 dark:bg-green-900/20">
              <span className="text-sm font-medium text-green-900 dark:text-green-200">
                {selected.size} selecionada{selected.size === 1 ? "" : "s"}
              </span>

              <button
                onClick={() => handleBulk({ acknowledge: true }, "marcadas como vistas")}
                disabled={bulkBusy}
                className="flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-sm text-white transition-colors hover:bg-green-700 disabled:opacity-60"
              >
                <EyeOff className="h-4 w-4" />
                Marcar como vistas
              </button>

              <button
                onClick={() => handleBulk({ acknowledge: false }, "de volta à fila")}
                disabled={bulkBusy}
                className="flex items-center gap-1 rounded-lg border border-green-300 bg-white px-3 py-1.5 text-sm text-green-900 transition-colors hover:bg-green-100 disabled:opacity-60 dark:border-green-800 dark:bg-transparent dark:text-green-200"
              >
                <Undo2 className="h-4 w-4" />
                Desmarcar
              </button>

              <button
                onClick={() => handleBulk({ enabled: false }, "fora da contagem")}
                disabled={bulkBusy}
                className="flex items-center gap-1 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm text-amber-800 transition-colors hover:bg-amber-50 disabled:opacity-60 dark:border-amber-800 dark:bg-transparent dark:text-amber-300"
              >
                <BellOff className="h-4 w-4" />
                Tirar da contagem
              </button>

              <button
                onClick={() => handleBulk({ enabled: true }, "de volta à contagem")}
                disabled={bulkBusy}
                className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-600 dark:bg-transparent dark:text-gray-300"
              >
                <AlarmClock className="h-4 w-4" />
                Voltar a contar
              </button>

              <button
                onClick={() => setSelected(new Set())}
                disabled={bulkBusy}
                className="ml-auto text-sm text-green-900 underline transition-colors hover:text-green-700 disabled:opacity-60 dark:text-green-200"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
              <p className="text-gray-600 dark:text-gray-400">
                {view === "overdue"
                  ? "Nenhum cliente fora do prazo agora."
                  : view === "never"
                    ? "Nenhum primeiro contato sem resposta."
                    : "Nenhuma conversa nesta lista."}
              </p>
            </div>
          ) : (
            items.map((item) => (
              <WaitingRow
                key={item.conversationId}
                item={item}
                slaMinutes={sla.minutes}
                busy={busyId === item.conversationId || bulkBusy}
                selected={selected.has(item.conversationId)}
                onToggleSelect={toggleSelect}
                onOpenConversation={onOpenConversation}
                onSlaChange={handleSlaChange}
              />
            ))
          )}
        </div>

        {summary.truncated && (
          <p className="mt-4 text-xs text-amber-600 dark:text-amber-400">
            Há mais conversas aguardando do que o limite exibido. Responda as mais
            antigas para liberar a lista.
          </p>
        )}
      </div>
    </div>
  );
};

export default WaitingResponse;
